import {
  IBreakdownFieldsFragment,
  IBucketDefFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { getBinningDisplay } from '../../../utils/binnings';
import useAnalysisBuilderInfo from '../../../redux/selectorHooks/useAnalysisBuilderInfo';

/**
 * A custom hook that parses breakdown groups and cohorts, and histogram
 * and returns an info string with following rules:
 *
 * 1. $Field # Field name when one field, eg “Browser”
 * 2. $Field1, $Field2  # Field names two fields, eg “Browser, Country”
 * 3. $Field { $Binning } # binning, without “i” icon, eg seconds_watched { Custom }, seconds_watched { Range 100 }
 * 4. $Field1, Segment  # “Segment” is the literal text when breakdown by one field + segments
 * 5. Segment  # “Segment” is the literal text when segments are present
 *
 * */

const parseBucketDef = (bucketDef: IBucketDefFieldsFragment) =>
  `{ ${getBinningDisplay(bucketDef)} }`;

const parseBreakdownValues = ({
  groupbys,
  cohortRefs,
}: IBreakdownFieldsFragment): string[] => {
  const result: string[] = [];

  if (groupbys?.length > 0) {
    groupbys.forEach(({ field, bucketDef }) => {
      if (bucketDef) {
        result.push(`${field.name} ${parseBucketDef(bucketDef)}`);
      } else {
        result.push(field.name);
      }
    });
  }

  if (cohortRefs?.length === 1) {
    result.push('Segment');
  } else if (cohortRefs?.length > 1) {
    cohortRefs.forEach((_, index) => {
      result.push(`Segment ${index + 1}`);
    });
  }

  return result;
};

export default (analysisId: string) => {
  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  const builderInfo = analysisBuilderInfo?.query;

  const result: string[] = [];

  // breakdown part
  if (builderInfo && 'breakdown' in builderInfo && builderInfo.breakdown) {
    result.push(...parseBreakdownValues(builderInfo.breakdown));
  }

  // histogram part
  if (
    builderInfo &&
    'subject' in builderInfo &&
    'subjectBucketDef' in builderInfo &&
    builderInfo.subject?.name &&
    builderInfo.subjectBucketDef
  ) {
    result.push(
      `
      ${builderInfo.subject.name}
      ${parseBucketDef(builderInfo.subjectBucketDef)}
      `,
    );
  }

  if (!result?.length) {
    return null;
  }

  return result.join(', ');
};
